/* eslint-disable import/extensions */
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";

export class UserSectionsDeliveryDateClass {
  #deliveryDateCache = {};

  #favoritesSelector = ".js-favorites-swiper";

  #orderedItemsSelector = ".js-ordered-items-swiper";

  #savedProjectSelector = ".js-saved-project-delivery";

  #deliveryDateSelector = ".js-delivery-date";

  #deliveryLoadingSelector = ".js-delivery-loading";

  #deliveryLoadedSelector = ".js-delivery-loaded";

  constructor() {
    this.#initZipCodeListener();
  }

  static zipcode = "session";

  get #$favoritesContainer() {
    return document.querySelector(this.#favoritesSelector);
  }

  get #$orderedItemsContainer() {
    return document.querySelector(this.#orderedItemsSelector);
  }

  static #getCacheKey(compositionId, isMade) {
    return `${compositionId}_${isMade ? "made" : "blank"}`;
  }

  #getCachedDeliveryDate(compositionId, isMade) {
    const zipCode = UserSectionsDeliveryDateClass.zipcode;
    if (!this.#deliveryDateCache[zipCode]) return null;

    const cacheKey = UserSectionsDeliveryDateClass.#getCacheKey(compositionId, isMade);
    return this.#deliveryDateCache[zipCode][cacheKey];
  }

  #setCachedDeliveryDate(compositionId, isMade, date) {
    const zipCode = UserSectionsDeliveryDateClass.zipcode;

    if (!this.#deliveryDateCache[zipCode]) {
      this.#deliveryDateCache[zipCode] = {};
    }

    const cacheKey = UserSectionsDeliveryDateClass.#getCacheKey(compositionId, isMade);
    this.#deliveryDateCache[zipCode][cacheKey] = { date };
  }

  #clearCache() {
    Object.keys(this.#deliveryDateCache).forEach(key => {
      delete this.#deliveryDateCache[key];
    });
  }

  #updateDeliveryDateUI(item, date) {
    const $dateElement = item.querySelector(this.#deliveryDateSelector);

    if ($dateElement) {
      $dateElement.textContent = date;

      const $loadingElement = item.querySelector(this.#deliveryLoadingSelector);
      const $loadedElement = item.querySelector(this.#deliveryLoadedSelector);

      if ($loadingElement) $loadingElement.classList.add("hidden");
      if ($loadedElement) $loadedElement.classList.remove("hidden");
    }
  }

  async #fetchAndUpdateDeliveryDate(item, isMade = false) {
    const { compositionId } = item.dataset;
    if (!compositionId) return;

    const cached = this.#getCachedDeliveryDate(compositionId, isMade);
    if (cached) {
      this.#updateDeliveryDateUI(item, cached.date);
      return;
    }

    try {
      const data = await api.product.fetchCompositionEstimates({
        composition_ids: compositionId,
        ...(isMade && { made: "true" })
      });

      if (data?.estimates?.[compositionId]) {
        const { date } = data.estimates[compositionId];

        this.#setCachedDeliveryDate(compositionId, isMade, date);
        this.#updateDeliveryDateUI(item, date);
      }
    } catch (error) {
      /* empty */
    }
  }

  #initSectionDeliveryDates(sectionSelector, isMade = false) {
    const $container = document.querySelector(sectionSelector);
    if (!$container) return;

    const $activeSlide = $container.querySelector(".is-selected, .carousel-cell");
    if (!$activeSlide) return;

    const $item = $activeSlide.querySelector("[data-composition-id]");
    if ($item) {
      this.#fetchAndUpdateDeliveryDate($item, isMade);
    }
  }

  #initZipCodeListener() {
    EventBus.on(Events.ZIPCODE_UPDATED, () => {
      this.#clearCache();
      this.init();
    });
  }

  #initSavedProjectDeliveryDate() {
    const $container = document.querySelector(this.#savedProjectSelector);
    if (!$container) return;

    try {
      const projectData = JSON.parse($container.dataset.projectData);
      const compositionId = projectData?.active_composition_id;

      if (compositionId) {
        $container.dataset.compositionId = compositionId;
        this.#fetchAndUpdateDeliveryDate($container, true);
      }
    } catch (error) {
      /* empty */
    }
  }

  init() {
    this.#initSectionDeliveryDates(this.#favoritesSelector, false);
    this.#initSectionDeliveryDates(this.#orderedItemsSelector, true);
    this.#initSavedProjectDeliveryDate();
  }

  setupSwiperListeners(swiper, sectionId) {
    const isMade = sectionId === "ordered-items";

    swiper.on("slideChange", () => {
      const $activeSlide = swiper.slides[swiper.activeIndex];
      const $item = $activeSlide?.querySelector("[data-composition-id]");
      if ($item) {
        this.#fetchAndUpdateDeliveryDate($item, isMade);
      }
    });
  }

  setupCarouselListeners(flickity, sectionId) {
    const isMade = sectionId === "ordered-items";

    flickity.on("change", index => {
      const $activeSlide = flickity.cells[index]?.element;
      const $item = $activeSlide?.querySelector("[data-composition-id]");
      if ($item) {
        this.#fetchAndUpdateDeliveryDate($item, isMade);
      }
    });
  }
}

export default () => new UserSectionsDeliveryDateClass();
